"use client";

import { useState } from "react";
import { Task } from "../../api/referralApi";
import { XMarkIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { extractLinkedInPostContent } from "../../api/linkedInExtractor";
import { toast } from "react-toastify";

interface LinkedInModalProps {
  task: Task;
  onSubmit: (postUrl: string, postContent: string) => Promise<boolean>;
  onClaimReward: () => Promise<void>;
  onClose: () => void;
}

const LinkedInModal = ({ task, onSubmit, onClaimReward, onClose }: LinkedInModalProps) => {
  const [postUrl, setPostUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isRejected, setIsRejected] = useState(false);

  const handleSubmit = async () => {
    if (!postUrl.trim()) {
      toast.error("Please enter a LinkedIn post URL");
      return;
    }

    try {
      setIsSubmitting(true);
      setIsRejected(false);
      
      // Step 1: Extract content
      const result = await extractLinkedInPostContent(postUrl.trim());
      
      if (result.success && result.content) {
        // Step 2: Submit to backend

        const success = await onSubmit(postUrl.trim(), result.content);
        // console.log("Submission result:", result.content);
        
        // If success is false (rejected), we do NOT set isSubmitted to true
        // The button remains enabled so user can try again
        if (success) {
          setIsSubmitted(true);
        } else {
          // Show rejected state in button
          setIsRejected(true);
          // Revert after 4 seconds
          setTimeout(() => {
            setIsRejected(false);
          }, 4000);
        }
      } else {
        toast.error(result.error || "Failed to extract post content");
      }
    } catch (error) {
      // console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClaimHelpers = async () => {
      try {
        setIsClaiming(true);
        await onClaimReward();
        // We do NOT call onClose() here. The parent component handles closing on success.
        // This allows error handling (toast) to happen without closing the modal if claim fails.
      } catch (error) {
        // console.error("Claim failed", error);
      } finally {
        setIsClaiming(false);
      }
  };

  const openSamplePosts = () => {
    window.open("/referral/linkedin-samples", "_blank");
  };

  const openLinkedIn = () => {
    window.open("https://www.linkedin.com/feed/", "_blank");
  };

  const getRewardText = () => {
    if (task.minRewardValue === task.maxRewardValue) {
      return `${task.minRewardValue} Coins`;
    }
    return `${task.minRewardValue} - ${task.maxRewardValue} Coins`;
  };

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 z-[100] bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none overflow-y-auto">
        <div className="flex flex-col sm:flex-row w-full max-w-6xl gap-6 pointer-events-auto my-auto" onClick={(e) => e.stopPropagation()}>
        {/* Left side - Small task card */}
        <div className="w-full sm:w-80 sm:shrink-0 h-auto sm:h-48 rounded-xl border border-gray-200 bg-white/90 p-6 shadow-lg flex flex-col">
          <div className="mb-4 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-[#0077B5] p-1 text-white flex items-center justify-center">
              <Image 
              src="/icons/referral/linkedin.svg" 
              alt="LinkedIn" 
              width={16} 
              height={16} 
               />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              LinkedIn Shoutout
            </h3>
          </div>
          
          <p className="mb-4 text-sm text-gray-600 flex-grow">
            Share your experience with your network and earn rewards!
          </p>
          
          <p className="mb-4 text-sm font-medium text-gray-900">
            <span className="font-semibold">Reward:</span> {getRewardText()} (based on eventual engagement)
          </p>
          
          {/* <button
            onClick={handleClaimHelpers}
            disabled={!isSubmitted || isClaiming}
            className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-medium transition-colors ${
              isSubmitted 
                ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer shadow-md animate-pulse" 
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isClaiming ? "Claiming..." : "Claim Reward"}
            {!isClaiming && <ArrowTopRightOnSquareIcon className="h-5 w-5" />}
          </button> */}
        </div>

        {/* Right side */}
        <div className="flex-1 rounded-xl bg-white p-8 shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Complete Task to Earn {getRewardText()}
            </h2>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Step 1 */}
          <div className="mb-8">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              (1) PREPARE CONTENT
            </h3>
            <p className="mb-4 text-gray-600">
              If you had a good experience, it would mean a lot if you could share a short post about it.
You can simply copy one of the sample posts and tweak it if you’d like. Thank you for the support! 
            </p>
            <button
              onClick={openSamplePosts}
              className="flex items-center text-center justify-center gap-2 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              View Sample Posts (Opens new tab)
            </button>
          </div>

          {/* Step 2 */}
          <div className="mb-8">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              (2) POST ON LINKEDIN
            </h3>
            <p className="mb-4 text-gray-600">
              Click below to open LinkedIn. Paste your content and publish. 
              <br/>
              <span className="font-semibold text-gray-800">
                 CRITICAL: Make Sure you have included one of the attachment present in sample posts.
              </span>
            </p>
            <button
              onClick={openLinkedIn}
              className="flex items-center justify-center w-full gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              Go to LinkedIn & Post (Opens new tab)
            </button>
          </div>

          {/* Step 3 */}
          <div>
            <h3 className="mb-3 text-lg font-semibold text-gray-900">
              (3) SUBMIT POST LINK
            </h3>
            <p className="mb-4 text-gray-600">
              Please paste the LinkedIn post URL you shared about ResumeMate below.
            </p>
            <div className="space-y-4">
              <div>
                <input
                  type="url"
                  value={postUrl}
                  onChange={(e) => setPostUrl(e.target.value)}
                  placeholder="https://linkedin.com/posts/..."
                  disabled={isSubmitting || isSubmitted}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={!postUrl.trim() || isSubmitting || isSubmitted}
                className={`w-full rounded-lg px-6 py-3 font-medium transition-colors ${
                  isRejected
                    ? "bg-red-600 text-white cursor-pointer"
                    : postUrl.trim() && !isSubmitting && !isSubmitted
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : isSubmitted 
                      ? "bg-green-600 text-white cursor-default"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span>Submitting...</span>
                  </div>
                ) : isRejected ? (
                  <div className="flex flex-col items-center justify-center">
                    <span className="font-semibold">⚠️ Please retry with a valid post</span>
                    {/* <span className="text-sm mt-1"></span> */}
                  </div>
                ) : isSubmitted ? (
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>Submitted Successfully</span>
                  </div>
                ) : (
                  "Submit for Verification"
                )}
              </button>
              
              {isSubmitted && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg text-green-800 text-center animate-fade-in">
                    <p className="font-semibold">Verification Successful!</p>
                    <p className="text-sm">Please click the "Claim Reward" button on the left to start your earnings timer.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LinkedInModal;